import { Component, OnInit, OnDestroy, Renderer2, HostListener, ViewChild, ElementRef } from '@angular/core';
import Discussion from 'src/app/interfaces/Discussion';
import { ActivatedRoute, ParamMap } from '@angular/router';
import { switchMap } from 'rxjs/operators';
import { ForumService } from 'src/app/services/forum.service';
import { Observable, Subscription } from 'rxjs';
import { MailService } from 'src/app/services/mail.service';
import Comment from 'src/app/interfaces/Comment';
import { UserService } from 'src/app/services/user.service';
import smoothscroll from 'smoothscroll-polyfill';
import { LoadingService } from 'src/app/services/loading.service';
import { ResourceService } from 'src/app/services/resource.service';
import { UploadService } from 'src/app/services/upload.service';
import { NotificationsService } from 'src/app/services/notifications.service';
import { AnalyticsService } from 'src/app/services/analytics.service';
import { UrlService } from 'src/app/services/url.service';

@Component({
  selector: 'app-forum-single',
  templateUrl: './forum-single.component.html',
  styleUrls: ['./forum-single.component.scss'],
})
export class ForumSingleComponent implements OnInit, OnDestroy {
  @ViewChild('timelineWrap', { static: false }) timelineWrap: ElementRef;
  @ViewChild('sticky', { static: false }) sticky: ElementRef;
  @ViewChild('timeline', { static: false }) timeline: ElementRef;
  @ViewChild('timelineInfo', { static: false }) timelineInfo: ElementRef;
  @ViewChild('newCommentEl', { static: false }) newCommentEl: ElementRef;
  subscriptions: Subscription = new Subscription();
  currentUser;
  discussion$: Observable<any>;
  discussion: Discussion;
  comments;
  newComment: Comment = {
    attachment: null,
    content: null,
    discussion: null,
    id: null,
    owner: null,
    ownerMeta: null,
    timestamp: null,
    reactions: [],
    replies: [],
    replyTo: null,
    newReply: null,
  };
  loading = false;
  quillModules = {
    toolbar: [['link', 'bold', 'italic', 'underline', 'strike', 'blockquote', { list: 'ordered' }, { list: 'bullet' }]],
  };
  enterBindings = [];
  members = [];
  taggedMembers = [];
  cardData;
  commentFile: File = null;
  replyFile: File = null;
  commentFilePreviewURL;
  replyFilePreviewURL;
  openAccessCheckboxComment = false;
  openAccessCheckboxReply = false;
  scrollComments = [];
  currentScrollComment;
  locked = false;

  constructor(
    private route: ActivatedRoute,
    private forumService: ForumService,
    private mailService: MailService,
    private userService: UserService,
    private renderer: Renderer2,
    private loadingService: LoadingService,
    private resourceService: ResourceService,
    private uploadService: UploadService,
    private notificationService: NotificationsService,
    private analyticsService: AnalyticsService,
    private urlService: UrlService,
  ) {}

  ngOnInit() {
    this.analyticsService.event('forum_discussion_view');
    smoothscroll.polyfill();
    this.discussion$ = this.route.paramMap.pipe(
      switchMap((params: ParamMap) => this.forumService.getDiscussion(params.get('id')))
    );
    this.subscriptions.add(
      this.userService.user.subscribe((u) => {
        if (u && u.uid) {
          this.currentUser = u;
        } else {
          this.locked = true;
        }
      })
    );
    this.subscriptions.add(
      // Get discussion
      this.discussion$.subscribe((d) => {
        this.discussion = d;
        this.currentScrollComment = {
          date: this.discussion.timestamp.toDate(),
          index: 0,
        };

        // Add resource data to discussion
        if (this.discussion.resource) {
          this.resourceService.getResource(this.discussion.resource).then((r: any) => {
            if (r) {
              this.cardData = { ...this.discussion, resourceData: { ...r } };
            }
          });
        } else {
          this.cardData = { ...this.discussion };
        }

        // Get comments
        this.forumService.getComments(this.discussion.id).subscribe((commentsRes) => {
          this.comments = commentsRes;
          // if (this.comments.length && (!this.discussion.comments || !this.discussion.comments.length)) {
          //   this.discussion.comments = this.comments.map((c) => {
          //     return { id: c.id, name: c.ownerMeta.name, uid: c.owner }
          //   })
          //   this.forumService.updateDiscussion(this.discussion.id, this.discussion);
          // }

          // Add resource data to comments
          this.comments.forEach((c) => {
            let commentData;

            if (c.resource) {
              this.resourceService.getResource(c.resource).then((r: any) => {
                if (r) {
                  commentData = { ...c, resourceData: { ...r } };
                  this.updateThreads();
                }
              });
            } else {
              commentData = { ...c };
            }
          });

          this.updateThreads();
        });
      })
    );
    this.subscriptions.add(this.userService.getMembers().subscribe((m) => (this.members = m)));

    // If coming from a notification with the url param 'comment'
    if (window.location.search.includes('comment')) {

      let commentStart = window.location.search.indexOf('comment=') + 8;
      let replyIndex = window.location.search.indexOf('&reply');
      let commentEnd = replyIndex !== -1 ? replyIndex : window.location.search.length;
      let commentId = window.location.search.substring(commentStart, commentEnd);
      let replyId = replyIndex === -1 ? null : window.location.search.substring(replyIndex + 7);

      let waitForComment = setInterval(() => {
        let commentNode = document.querySelector(`[data-card-id="${commentId}"]`);

        if (commentNode) {
          clearInterval(waitForComment);

          if (replyId) {
            let expandCta: HTMLElement = commentNode.querySelector('.reply.expand-cta') as HTMLElement;
            expandCta.click();

            let waitForReply = setInterval(() => {
              let replyNode = document.querySelector(`[data-card-id="${replyId}"]`);

              if (replyNode) {
                clearInterval(waitForReply);
                this.highlightAndScroll(replyNode);
              }
            }, 100)
          } else {
            this.highlightAndScroll(commentNode);
          }
    
        }
      }, 100)
    }
  }

  editorOpened(editor) {
    this.enterBindings = editor.quillEditor.keyboard.bindings[13].map((b) => Object.assign({}, b))
    delete editor.quillEditor.keyboard.bindings[13]
  }
  editorClosed(editor) {
    if (!editor.quillEditor.keyboard.bindings[13]) {
      editor.quillEditor.keyboard.bindings[13] = this.enterBindings.map((b) => Object.assign({}, b))
    }
  }

  highlightAndScroll(node) {
    const headerOffset = 150;
    let targetPosition = node.getBoundingClientRect().top;
    let offsetPosition = targetPosition - headerOffset;
    let ocCard = node.querySelector('.oc-card');
    ocCard.classList.add('highlight');
    setTimeout(() => ocCard.classList.remove('highlight'), 2000);

    window.scrollTo({
        top: offsetPosition,
        behavior: "smooth"
    });
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  postComment(comment = null) {
    this.loading = true;
    this.loadingService.startLoading();
    let postComment = comment ? comment.newReply : this.newComment;
    postComment.content = this.urlService.processContent(postComment.content, this.taggedMembers);

    postComment.discussion = this.discussion.id;
    postComment.replyTo = comment ? comment.id : null;
    postComment.owner = this.currentUser.uid;
    postComment.ownerMeta = {
      name: this.currentUser.displayName,
      organization: this.currentUser.organization ? this.currentUser.organization : null,
    };
    postComment.timestamp = new Date();
    let attachment = this[comment ? 'replyFile' : 'commentFile'];
    if (attachment) {
      postComment.attachment = attachment;
    }
    if (postComment.replyTo) {
      this.analyticsService.event('forum_discussion_sub_reply');
    } else {
      this.analyticsService.event('forum_discussion_reply');
    }
    if (attachment) {
      this.uploadService.uploadFile(attachment).then((downloadUrl) => {
        postComment.attachment = {
          name: attachment.name,
          url: downloadUrl,
          size: attachment.size,
        };
        attachment = null;
        this.post(postComment);
      });
    } else {
      this.post(postComment);
    }
  }

  post(postComment) {
    this.forumService
      .postComment(postComment)
      .then((cRes: any) => {
        if (this.taggedMembers.length > 0) {
          this.taggedMembers.forEach((tm) => {
            if (postComment.content.indexOf('@' + tm.displayName) > -1) {
    
              this.notificationService.saveNotification(
                tm.uid,
                this.currentUser.uid,
                `mentioned you in a <a href="forum/${this.discussion.id}?comment=${cRes.id}">discussion</a>`,
                this.currentUser.displayName,
                this.currentUser.profilePicture
              );
    
              this.notificationService.sendNotifications({
                type: 'directMentions',
                mention: tm.uid,
                comment: this.currentUser.displayName,
                url: `/forum/${this.discussion.id}?comment=${cRes.id}`,
              });
            }
          });
        }
        this.taggedMembers = [];

        this.resetComment();
        cRes.get().then(comment => {

          const discussionComments = this.discussion.comments ? this.discussion.comments : [];
          discussionComments.push({ 
            id: cRes.id, 
            name: this.currentUser.displayName, 
            uid: this.currentUser.uid, 
            profilePicture: this.currentUser.profilePicture,
            timestamp: comment.data().timestamp 
          });
          this.forumService.updateDiscussion(this.discussion.id, { comments: discussionComments });
        })

        this.currentUser.comments.push(cRes.id);
        let commentsArr = [...this.currentUser.comments];
        this.userService.saveUser(this.currentUser.uid, this.currentUser).then((res) => {
          this.currentUser.comments = [...commentsArr];
        });

        this.loading = false;
        this.loadingService.stopLoading();

        // Send Notifications
        let notifStr = postComment.replyTo ? 
          `just replied to your <a href="forum/${this.discussion.id}?comment=${postComment.replyTo}&reply=${cRes.id}">comment</a>` :
          `just replied to your <a href="forum/${this.discussion.id}?comment=${cRes.id}">discussion</a>`;

        this.notificationService.saveNotification(
          this.discussion.owner,
          this.currentUser.uid,
          notifStr,
          this.currentUser.displayName,
          this.currentUser.profilePicture
        );
        this.notificationService.sendNotifications({
            type: 'replies',
            author: this.discussion.owner,
            comment: this.currentUser.displayName,
            url: `/forum/${this.discussion.id}?comment=${cRes.id}`,
          });
        this.notificationService.sendNotifications({
          type: 'followedDiscussions',
          discussionId: this.discussion.id,
          url: `/forum/${this.discussion.id}?comment=${cRes.id}`,
          displayName: this.currentUser.displayName,
          content: postComment.content
        });


      })
      .catch((cError) => {
        console.error(cError);
        this.loading = false;
        this.loadingService.stopLoading();
      });
  }

  /* For likes, replies */
  cardEvent(e, comment, expand = null) {
    if (e.type === 'update_comment') {
      this.forumService.updateComment(e.id, e.update);
    } else if (e.type === 'update_discussion') {
      this.forumService.updateDiscussion(e.id, e.update);
    } else if (e.type === 'reply_comment') {
      if (e.reply_to === 'main') {
        // Scroll to editor
        const replyMain = this.renderer.selectRootElement('.comment-scroll-target');
        this.scrollToElement(replyMain);
        setTimeout(() => {
          const editorMain = this.renderer.selectRootElement('.new-comment-card .ql-editor');
          setTimeout(() => {
            editorMain.focus();
          }, 10);
        }, 1000);
      } else if (e.reply_to === 'comment') {
        if (expand) {
          expand.expanded = true;
        }
        comment.newReply = this.setNewReply();
        setTimeout(() => {
          const replyComment = e.cardEl.querySelector('.reply-scroll-target');
          this.scrollToElement(replyComment);
          setTimeout(() => {
            setTimeout(() => {
              const editorReply = replyComment.nextElementSibling.querySelector('.ql-editor');
              editorReply.focus();
            }, 500);
          }, 10);
        }, 10);
      }
    } else if (e.type === 'delete_comment') {
      this.forumService.deleteComment(e.id).then(() => {
        this.deleteExtraCommentData(e.id);
      })
    }
  }

  deleteExtraCommentData(id) {
    this.forumService.getComment(id).then((comment: Comment) => { 
      this.forumService.getDiscussion(comment.id).then((discussion: any) => {
        discussion.comments.forEach((c, i) => {
          if (id === c.id) {
            discussion.comments.slice(i);
          }
        })
        this.forumService.updateDiscussion(discussion.id, { comments: discussion.comments }).then(() => {
          this.userService.user.subscribe((u) => {
            if (u) {
              u.comments.slice(u.comments.indexOf(id));
              this.userService.saveUser(u.uid, u).then(() => {
                this.forumService.getAllComments().subscribe((comments) => {
                  if (comments) {
                    comments.forEach(c => {
                      if (c.replyTo === id) {
                        this.forumService.deleteComment(c.id).then(() => {
                          this.deleteExtraCommentData(c.id);
                        })
                      }
                    })
                  }
                })
              });
            }
          })
        });
      })
    })
  }

  toggleReplies(r) {
    r.expanded = !r.expanded;
    setTimeout(() => {
      this.onWindowScroll();
    });
  }

  resetComment() {
    this.newComment = {
      attachment: null,
      content: null,
      discussion: null,
      id: null,
      owner: null,
      ownerMeta: null,
      timestamp: null,
      reactions: [],
      newReply: null,
      replies: [],
      replyTo: null,
    };
    this.commentFile = null;
    this.taggedMembers = [];
  }

  setNewReply() {
    this.replyFile = null;
    return {
      attachment: null,
      content: null,
      id: null,
      owner: this.currentUser.uid,
      ownerMeta: null,
      timestamp: null,
      reactions: [],
      replies: [],
      newReply: null,
    };
  }

  scrollToTop() {
    this.scrollToElement(document.querySelector('.discussion-wrap'));
  }

  scrollToLast() {
    if (document.querySelector('.last')) {
      this.scrollToElement(document.querySelector('.last'));
    } else {
      this.scrollToElement(document.querySelector('.comment-scroll-target.scroll-target'));
    }
  }

  scrollToElement(el): void {
    el.scrollIntoView({ behavior: 'smooth', block: 'start', inline: 'nearest' });
  }

  tagMember(m) {
    this.taggedMembers.push({ email: m.email, displayName: m.displayName, uid: m.uid });
  }

  updateThreads() {
    this.comments.forEach((c) => {
      this.comments.forEach((c2) => {
        if (c2.replyTo === c.id) {
          c.replies.push(c2);
        } else if (!c2.replyTo) {
          c2.newReply = this.setNewReply();
        }
      });
    });
  }

  upload(event, reply = false): void {
    if (event.target && event.target.files.length > 0) {
      if (event.target && event.target.files.length > 0) {
        var reader = new FileReader();
        var t = this;
        reader.onload = function (e) {
          t[(reply ? 'reply' : 'comment') + 'FilePreviewURL'] = reader.result;
        };
        if (event.target.files[0].name.endsWith('.pdf')) {
          let $img: any = document.querySelector('input[type="file"]');
          reader.readAsArrayBuffer($img.files[0]);
        }
        if (!event.target.files[0].name.endsWith('.pdf')) reader.readAsDataURL(event.target.files[0]); // convert to base64 string

        if (reply) {
          this.replyFile = event.target.files[0];
        } else {
          this.commentFile = event.target.files[0];
        }
      }
    }
  }

  removeFile(reply = false) {
    if (reply) {
      if (this.replyFile) {
        this.replyFile = null;
      }
    } else {
      if (this.commentFile) {
        this.commentFile = null;
      }
    }
  }

  @HostListener('window:scroll', ['$event'])
  onWindowScroll() {
    let timelineWrapBox, stickyBox, timelineBox, timelineInfoBox, newCommentBox;
    let y = window.pageYOffset;
    this.setScrollComments();
    if (this.timelineWrap && this.timelineWrap.nativeElement)
      timelineWrapBox = this.timelineWrap.nativeElement.getBoundingClientRect();
    if (this.sticky && this.sticky.nativeElement) stickyBox = this.sticky.nativeElement.getBoundingClientRect();
    if (this.timeline && this.timeline.nativeElement) timelineBox = this.timeline.nativeElement.getBoundingClientRect();
    if (this.timelineInfo && this.timelineInfo.nativeElement)
      timelineInfoBox = this.timelineInfo.nativeElement.getBoundingClientRect();
    if (this.newCommentEl && this.newCommentEl.nativeElement)
      newCommentBox = this.newCommentEl.nativeElement.getBoundingClientRect();

    if (timelineWrapBox && stickyBox && timelineBox && timelineInfoBox && newCommentBox) {
      const timelineScrollRange = timelineWrapBox.height - (newCommentBox.height + stickyBox.height + 24);
      if (timelineWrapBox.y > 120) {
        this.timelineInfo.nativeElement.style.marginTop = '0px';
      } else if (timelineWrapBox.y <= 120 && timelineWrapBox.y > 120 - timelineScrollRange) {
        let perc = ((timelineWrapBox.y - 120) / timelineScrollRange) * -1;
        this.timelineInfo.nativeElement.style.marginTop =
          (timelineBox.height - timelineInfoBox.height * 1.5) * perc + 'px';
      } else {
        this.timelineInfo.nativeElement.style.marginTop = timelineBox.height - timelineInfoBox.height * 1.5 + 'px';
      }
    }
  }

  setScrollComments() {
    let comments = document.querySelectorAll('.comment-wrap');
    comments.forEach((el: any, i) => {
      if (el.getBoundingClientRect().y < 200) {
        this.currentScrollComment = {
          date: el.dataset.cardDate,
          index: (() => {
            if (el.dataset.cardId === this.discussion.id) {
              return 0;
            } else {
              let ind;
              this.comments.forEach((c, i) => {
                if (el.dataset.cardId === c.id) ind = i + 1;
              });
              return ind;
            }
          })(),
        };
      }
    });
  }
}
